import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ImageBackground,
  Image,
  Platform,
} from 'react-native';
import PlatformIcon from '../common/PlatformIcon';
import { MODULE_COLORS } from '@cubemeet/core';
import type { Travel } from '@cubemeet/core';
import { getTravelBackgroundAsset } from '../../assets/imageRegistry';
import { getPresetAvatarAsset } from '../../assets/avatarRegistry';

type MemberProfile = NonNullable<NonNullable<Travel['members']>[number]['profile']>;

type SimpleMember = {
  id: string;
  displayName?: string | null;
  avatarUrl?: string | null;
};

export interface TravelCardActionButton {
  title: string;
  onPress: () => void;
  disabled?: boolean;
  style?: 'default' | 'disabled' | 'pending';
}

export interface TravelCardProps {
  travel: Travel;
  onPress?: () => void;
  onLikePress?: () => void;
  likeInfo?: {
    isLiked: boolean;
    count: number;
  };
  isLiking?: boolean;
  onChatPress?: () => void;
  actionButton?: TravelCardActionButton;
  /**
   * 允许外部覆盖成员渲染逻辑
   */
  renderMembers?: (members: SimpleMember[]) => React.ReactNode;
  /**
   * 自定义预算展示文本
   */
  budgetText?: string | null;
  /**
   * 自定义描述文本
   */
  descriptionText?: string | null;
  /**
   * 日期文本格式化函数
   */
  formatDate?: (dateString: string | null | undefined) => string;
  /**
   * 翻译函数
   */
  t?: (key: string, params?: Record<string, any>) => string;
  /**
   * 自定义聊天按钮渲染
   */
  renderChatButton?: (options: { onPress: () => void }) => React.ReactNode;
}

const defaultT = (key: string) => key;

// Attribution reminder for the web-chat sticker asset
// Hey! Remember you have to attribute inipagistudio
const ATTRIBUTION_NOTICE = 'Hey! Remember you have to attribute inipagistudio';

const ensureMembers = (members?: Travel['members']): SimpleMember[] => {
  if (!Array.isArray(members)) return [];
  return members
    .filter((member): member is NonNullable<typeof member> => Boolean(member))
    .map((member) => {
      const profile: MemberProfile | undefined = member?.profile ?? undefined;
      return {
        id: member.user_id || member?.profile?.display_name || Math.random().toString(36).slice(2),
        displayName: profile?.display_name ?? null,
        avatarUrl: profile?.avatar_url ?? null,
      };
    });
};

const getBackgroundImageSource = (url?: string | null) => {
  if (!url) {
    return getTravelBackgroundAsset();
  }

  if (url.startsWith('http')) {
    return { uri: url };
  }

  return getTravelBackgroundAsset(url);
};

const getMemberAvatarSource = (avatarUrl?: string | null) => {
  if (!avatarUrl) return undefined;

  if (avatarUrl.startsWith('http')) {
    return { uri: avatarUrl } as const;
  }

  if (avatarUrl.startsWith('preset_')) {
    return getPresetAvatarAsset(avatarUrl) ?? undefined;
  }

  return undefined;
};

const getStatusFromDates = (travel: Travel): 'planning' | 'active' | 'completed' | 'cancelled' | string => {
  if (travel.status === 'cancelled') return 'cancelled';
  if (!travel.start_date) return 'planning';

  try {
    const today = new Date();
    const startDate = new Date(travel.start_date);
    const endDate = travel.end_date ? new Date(travel.end_date) : startDate;

    today.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (today < startDate) return 'planning';
    if (today >= startDate && today <= endDate) return 'active';
    return 'completed';
  } catch (error) {
    return travel.status || 'planning';
  }
};

const getStatusLabel = (
  status: string,
  t: (key: string, params?: Record<string, any>) => string,
): string => {
  switch (status) {
    case 'planning':
      return t('travelCard.status.planning');
    case 'active':
      return t('travelCard.status.active');
    case 'completed':
      return t('travelCard.status.completed');
    case 'cancelled':
      return t('travelCard.status.cancelled');
    default:
      return status;
  }
};

const defaultFormatDate = (value: string | null | undefined) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString();
  } catch (error) {
    return value;
  }
};

const makeBudgetText = (
  travel: Travel,
  t: (key: string, params?: Record<string, any>) => string,
): string | null => {
  const { payment_mode, budget_per_person, currency = 'CNY' } = travel;

  if (payment_mode === 'actual_average' || payment_mode === 'actual_share') {
    return t('travelCard.info.actualCost', { defaultValue: 'Actual Cost' });
  }

  if (!budget_per_person) {
    return t('travelCard.info.noBudget');
  }

  const currencyMap: Record<string, string> = {
    CNY: '¥',
    HKD: 'HK$',
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    KRW: '₩',
    SGD: 'S$',
    TWD: 'NT$',
    MOP: 'MOP$',
  };

  const symbol = currencyMap[currency] || currency;
  return `${symbol}${budget_per_person}${t('travelCard.info.perPerson')}`;
};

const getInitials = (name?: string | null) => {
  if (!name) return 'U';
  const trimmed = name.trim();
  if (!trimmed) return 'U';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const TravelCard: React.FC<TravelCardProps> = ({
  travel,
  onPress,
  onLikePress,
  likeInfo = { isLiked: false, count: 0 },
  isLiking = false,
  onChatPress,
  actionButton,
  renderMembers,
  budgetText,
  descriptionText,
  formatDate = defaultFormatDate,
  t = defaultT,
  renderChatButton,
}) => {
  const status = useMemo(() => getStatusFromDates(travel), [travel]);
  const statusText = getStatusLabel(status, t);
  const members = useMemo(() => ensureMembers(travel.members), [travel.members]);

  const resolvedBudgetText = budgetText ?? makeBudgetText(travel, t) ?? undefined;
  const resolvedDescription = descriptionText === null
    ? undefined
    : descriptionText ?? travel.description ?? undefined;
  const imageSource = useMemo(() => getBackgroundImageSource(travel.background_image_url), [travel.background_image_url]);

  const locationText = travel.location || t('travelCard.info.noLocation');
  const memberCountText = t('travelCard.info.memberCount', { count: members.length });
  const dateText = `${formatDate(travel.start_date)} - ${formatDate(travel.end_date)}`;

  const renderDefaultMembers = () => {
    if (!members.length) return null;

    return (
      <View style={styles.cardMembersLeft}>
        <View style={styles.cardMembersAvatars}>
          {members.slice(0, 4).map((member, index) => {
            const source = getMemberAvatarSource(member.avatarUrl ?? undefined);
            return (
              <View
                key={`${member.id}-${index}`}
                style={[styles.cardMemberAvatar, index > 0 ? styles.cardMemberAvatarOverlap : undefined]}
              >
                {source ? (
                  <Image source={source} style={styles.cardMemberAvatarImage} resizeMode="cover" />
                ) : (
                  <Text style={styles.cardMemberAvatarInitial}>{getInitials(member.displayName)}</Text>
                )}
              </View>
            );
          })}
          {members.length > 4 ? (
            <View style={[styles.cardMemberAvatar, styles.cardMemberMore]}>
              <Text style={styles.cardMemberMoreText}>+{members.length - 4}</Text>
            </View>
          ) : null}
        </View>
        <Text style={styles.cardMembersCount}>{memberCountText}</Text>
      </View>
    );
  };

  const renderLikeButton = () => {
    if (!onLikePress) return null;
    return (
        <TouchableOpacity
        style={[styles.cardLikeButton, likeInfo.isLiked && styles.cardLikeButtonActive]}
        onPress={onLikePress}
        disabled={isLiking}
        activeOpacity={0.8}
      >
        <PlatformIcon name="favorite" size={20} color="#fff" family="MaterialIcons" />
        {likeInfo.count > 0 ? <Text style={styles.cardLikeCount}>{likeInfo.count}</Text> : null}
      </TouchableOpacity>
    );
  };

  const renderActionButton = () => {
    if (!actionButton) {
      return (
        <View style={[styles.cardStatusBadge, styles.cardStatusBadgeOverlay]}>
          <Text style={styles.cardStatusText}>{statusText}</Text>
        </View>
      );
    }

    const { style = 'default', disabled } = actionButton;

    return (
      <TouchableOpacity
        style={[
          styles.cardStatusBadge,
          styles.cardStatusBadgeOverlay,
          style === 'disabled' && styles.cardActionBadgeDisabled,
          style === 'pending' && styles.cardActionBadgePending,
        ]}
        onPress={actionButton.onPress}
        disabled={disabled}
        activeOpacity={0.8}
      >
        <Text style={styles.cardStatusText}>{actionButton.title}</Text>
      </TouchableOpacity>
    );
  };

  // Extract inner content so we can avoid rendering a nested <button> on web
  const content = (
    <>
      <ImageBackground source={imageSource} style={styles.cardImageBackground} imageStyle={styles.cardImageStyle}>
        <View
          style={[
            styles.cardOverlay,
            !travel.background_image_url && { backgroundColor: MODULE_COLORS.travel + '90' },
          ]}
        />

        <View style={styles.cardTitleOverlay}>
          <Text style={styles.cardTitleOverlayText} numberOfLines={1}>
            {travel.name}
          </Text>
        </View>

        <View style={styles.cardLocationContainer}>
            <View style={styles.cardLocationInfo}>
              <PlatformIcon name="location-on" size={16} color="#fff" family="MaterialIcons" />
              <Text style={styles.cardLocationText}>{locationText}</Text>
            </View>
        </View>

        <View style={styles.cardLikeContainer}>{renderLikeButton()}</View>

        <View style={styles.cardStatusContainer}>{renderActionButton()}</View>
      </ImageBackground>

      <View style={styles.cardInfoContainer}>
        {(members.length || onChatPress) ? (
          <View style={styles.cardTopRow}>
            <View
              style={[
                styles.cardMembersWrapper,
                !members.length && styles.cardMembersWrapperEmpty,
              ]}
            >
              {members.length ? (renderMembers ? renderMembers(members) : renderDefaultMembers()) : null}
            </View>

            {onChatPress ? (
              renderChatButton ? (
                renderChatButton({ onPress: onChatPress })
              ) : (
                <TouchableOpacity style={styles.chatButton} onPress={onChatPress} activeOpacity={0.9}>
                  {/* Web uses the sticker image asset; native falls back to the forum icon */}
                  {Platform.OS === 'web' ? (
                    <Image source={{ uri: '/assets/icons/web-chat.png' }} style={styles.chatButtonImage} />
                  ) : (
                    <PlatformIcon name="forum" size={16} color={MODULE_COLORS.travel} family="MaterialIcons" />
                  )}
                  <Text style={styles.chatButtonText}>{t('travelCard.actions.enterChat', { defaultValue: 'Enter Chat' })}</Text>
                </TouchableOpacity>
              )
            ) : null}
          </View>
        ) : null}

        <View style={styles.cardInfoRows}>
          <View style={styles.cardInfoRow}>
            <PlatformIcon name="event" size={14} color="#666" family="MaterialIcons" />
            <Text style={styles.cardInfoText}>{dateText}</Text>
          </View>

          {resolvedBudgetText ? (
            <View style={styles.cardInfoRow}>
              <PlatformIcon name="account-balance-wallet" size={14} color="#666" family="MaterialIcons" />
              <Text style={styles.cardInfoText}>{resolvedBudgetText}</Text>
            </View>
          ) : null}
        </View>

        {resolvedDescription ? (
          <Text style={styles.descriptionText} numberOfLines={2}>
            {resolvedDescription}
          </Text>
        ) : null}
      </View>
    </>
  );

  // On web the default TouchableOpacity renders to a <button>. Nesting buttons makes inner clicks unreliable.
  // So only render TouchableOpacity when an onPress is provided; otherwise use a plain View as the wrapper.
  if (onPress) {
    return (
      <TouchableOpacity
        style={styles.groupCard}
        onPress={onPress}
        activeOpacity={onPress ? 0.95 : 1}
        disabled={!onPress}
      >
        {content}
      </TouchableOpacity>
    );
  }

  return (
    <View style={styles.groupCard}>
      {content}
    </View>
  );
};

const styles = StyleSheet.create({
  groupCard: Platform.select({
    web: {
      marginBottom: 20,
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      backgroundColor: '#FFFFFF',
    },
    default: {
      marginBottom: 20,
      borderRadius: 16,
      overflow: 'hidden',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.15,
      shadowRadius: 12,
      elevation: 8,
      backgroundColor: '#FFFFFF',
    },
  }),
  cardImageBackground: {
    width: '100%',
    height: 200,
    position: 'relative',
  },
  cardImageStyle: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  cardOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  cardTitleOverlay: {
    position: 'absolute',
    top: 16,
    left: 16,
    maxWidth: '60%',
  },
  cardTitleOverlayText: Platform.select({
    web: {
      color: '#fff',
      fontSize: 18,
      fontWeight: '700',
      textShadow: '0 1px 4px rgba(0,0,0,0.8)',
      letterSpacing: 0.5,
    },
    default: {
      color: '#fff',
      fontSize: 18,
      fontWeight: '700',
      textShadowColor: 'rgba(0, 0, 0, 0.8)',
      textShadowOffset: { width: 0, height: 1 },
      textShadowRadius: 4,
      letterSpacing: 0.5,
    },
  }),
  cardLocationContainer: {
    position: 'absolute',
    bottom: 16,
    left: 16,
  },
  cardLocationInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  cardLocationText: Platform.select({
    web: {
      color: '#fff',
      fontSize: 13,
      fontWeight: '600',
      marginLeft: 4,
      textShadow: '0 1px 2px rgba(0,0,0,0.8)',
    },
    default: {
      color: '#fff',
      fontSize: 13,
      fontWeight: '600',
      marginLeft: 4,
      textShadowColor: 'rgba(0, 0, 0, 0.8)',
      textShadowOffset: { width: 0, height: 1 },
      textShadowRadius: 2,
    },
  }),
  cardLikeContainer: {
    position: 'absolute',
    top: 16,
    right: 16,
  },
  cardLikeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  cardLikeButtonActive: {
    backgroundColor: MODULE_COLORS.travel,
  },
  cardLikeCount: {
    color: '#fff',
    fontSize: 12,
    marginLeft: 4,
  },
  cardStatusContainer: {
    position: 'absolute',
    bottom: 16,
    right: 16,
  },
  cardStatusBadge: {
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.25)',
  },
  cardStatusBadgeOverlay: {
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  cardStatusText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.4,
  },
  cardActionBadgeDisabled: {
    opacity: 0.6,
  },
  cardActionBadgePending: {
    backgroundColor: 'rgba(255, 159, 67, 0.9)',
  },
  cardInfoContainer: {
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  cardTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  cardMembersWrapper: {
    flex: 1,
    paddingRight: 12,
  },
  cardMembersWrapperEmpty: {
    flex: 0,
    paddingRight: 0,
  },
  cardMembersLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardMembersAvatars: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardMemberAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#E5E7EB',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#fff',
  },
  cardMemberAvatarOverlap: {
    marginLeft: -6,
  },
  cardMemberAvatarImage: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  cardMemberAvatarInitial: {
    color: '#374151',
    fontWeight: '600',
    fontSize: 14,
  },
  cardMemberMore: {
    backgroundColor: MODULE_COLORS.travel,
    marginLeft: -6,
  },
  cardMemberMoreText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  cardMembersCount: {
    marginLeft: 12,
    fontSize: 13,
    color: '#4B5563',
    fontWeight: '600',
  },
  cardInfoRows: {
    marginBottom: 8,
  },
  cardInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  cardInfoText: {
    marginLeft: 8,
    fontSize: 13,
    color: '#374151',
  },
  descriptionText: {
    fontSize: 13,
    color: '#4B5563',
    lineHeight: 18,
    marginBottom: 12,
  },
  chatButton: {
    ...Platform.select({
      web: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 20,
        // use CSS boxShadow on web (react-native-web warns about shadow* props)
        boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
      },
      default: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 20,
        shadowColor: '#000',
        shadowOpacity: 0.08,
        shadowOffset: { width: 0, height: 2 },
        shadowRadius: 6,
        elevation: 4,
      }
    }),
  },
  chatButtonText: {
    marginLeft: 8,
    fontSize: 13,
    fontWeight: '700',
    color: MODULE_COLORS.travel,
  },
  chatButtonImage: {
    width: 20,
    height: 20,
    borderRadius: 4,
  },
});

export default TravelCard;
